// Mapa de calor: dónde se concentraron personas / máquinas / vehículos en el período.
// Extra sobre Enlaps: filtro propio por familia y "Evolución" (un mapa por día o por semana).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FAMILIES, classInfo, isPerson, visibleDets, photoUrl, dayKey, fmtKey, addDays } from './iaData';
import { T, Btn, Seg } from './ui';

// Paleta jet (azul → cian → verde → amarillo → rojo)
const jet = (v) => {
  const r = Math.min(1, Math.max(0, 1.5 - Math.abs(4 * v - 3)));
  const g = Math.min(1, Math.max(0, 1.5 - Math.abs(4 * v - 2)));
  const b = Math.min(1, Math.max(0, 1.5 - Math.abs(4 * v - 1)));
  return [r * 255, g * 255, b * 255];
};

function accumulate(photos, cfg, fam, W, H) {
  const grid = new Float32Array(W * H);
  // Con muchas fotos alcanza una muestra pareja (el mapa es una densidad, no un conteo)
  const step = Math.max(1, Math.ceil(photos.length / 400));
  for (let pi = 0; pi < photos.length; pi += step) {
    const p = photos[pi];
    if (!p.hasBoxes) continue;
    const sx = W / p.w, sy = H / p.h;
    for (const d of visibleDets(p, cfg)) {
      if (d.static) continue; // lo que no se movió entre fotos (postes, máquinas estacionadas) no es actividad
      if (fam !== 'todo' && classInfo(d.cls).family !== fam) continue;
      const cx = ((d.x1 + d.x2) / 2) * sx;
      const cy = (isPerson(d.cls) ? d.y2 - (d.y2 - d.y1) * 0.15 : (d.y1 + d.y2) / 2) * sy; // personas: donde pisan
      const r = Math.max(4, Math.min(W / 14, Math.max(d.x2 - d.x1, d.y2 - d.y1) * sx * 0.7));
      const r2 = r * r, x0 = Math.max(0, Math.floor(cx - r)), x1 = Math.min(W - 1, Math.ceil(cx + r));
      const y0 = Math.max(0, Math.floor(cy - r)), y1 = Math.min(H - 1, Math.ceil(cy + r));
      for (let y = y0; y <= y1; y++) {
        const dy = y - cy;
        for (let x = x0; x <= x1; x++) {
          const dx = x - cx, q = (dx * dx + dy * dy) / r2;
          if (q < 1) grid[y * W + x] += Math.exp(-q * 3);
        }
      }
    }
  }
  return grid;
}

function paint(canvas, grid, W, H, gamma) {
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  // Escala con el percentil 99,5 para que un solo punto caliente no apague el resto
  const nz = [];
  for (let i = 0; i < grid.length; i += 3) if (grid[i] > 0) nz.push(grid[i]);
  nz.sort((a, b) => a - b);
  const max = nz.length ? nz[Math.min(nz.length - 1, Math.floor(nz.length * 0.995))] : 0;
  const img = ctx.createImageData(W, H);
  if (max > 0) {
    for (let i = 0; i < grid.length; i++) {
      const v = Math.min(1, Math.pow(grid[i] / max, gamma));
      if (v < 0.14) continue;
      const [r, g, b] = jet(v);
      img.data[i * 4] = r; img.data[i * 4 + 1] = g; img.data[i * 4 + 2] = b;
      img.data[i * 4 + 3] = Math.min(255, 30 + v * 225);
    }
  }
  ctx.putImageData(img, 0, 0);
  return max;
}

export default function HeatmapPanel({ photos, cfg, rangeLabel }) {
  const [fam, setFam] = useState('todo');
  const [gamma, setGamma] = useState(0.8);
  const [opacity, setOpacity] = useState(0.65);
  const [mode, setMode] = useState('total');
  const [url, setUrl] = useState(null);
  const canvasRef = useRef(null);
  const [empty, setEmpty] = useState(false);

  // Fondo: la última foto de día del período
  const bg = useMemo(() => [...photos].reverse().find((p) => p.hasBoxes) || photos[photos.length - 1], [photos]);
  useEffect(() => {
    let alive = true;
    setUrl(null);
    if (bg) photoUrl(bg).then((u) => alive && setUrl(u)).catch(() => {});
    return () => { alive = false; };
  }, [bg]);

  const W = 320, H = bg ? Math.round((W * bg.h) / bg.w) : 180;
  const grid = useMemo(() => (bg ? accumulate(photos, cfg, fam, W, H) : null), [photos, cfg, fam, W, H, bg]);
  useEffect(() => {
    if (mode === 'total' && grid && canvasRef.current) setEmpty(paint(canvasRef.current, grid, W, H, gamma) === 0);
  }, [grid, gamma, W, H, mode]);

  // Evolución: un mapa por día (períodos cortos) o por semana
  const slices = useMemo(() => {
    if (!photos.length) return [];
    const first = dayKey(photos[0].ms), last = dayKey(photos[photos.length - 1].ms);
    const span = (Date.parse(last) - Date.parse(first)) / 864e5 + 1;
    const step = span <= 14 ? 1 : 7;
    const out = [];
    for (let k = first; k <= last && out.length < 12; k = addDays(k, step)) {
      const end = addDays(k, step - 1);
      const ph = photos.filter((p) => { const d = dayKey(p.ms); return d >= k && d <= end; });
      if (ph.length) out.push({ label: step === 1 ? fmtKey(k) : `${fmtKey(k)} → ${fmtKey(end)}`, photos: ph });
    }
    return out;
  }, [photos]);

  const download = async () => {
    const out = document.createElement('canvas');
    const OW = 1600, OH = Math.round((OW * bg.h) / bg.w);
    out.width = OW; out.height = OH;
    const ctx = out.getContext('2d');
    try {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.src = url;
      await im.decode();
      ctx.drawImage(im, 0, 0, OW, OH);
    } catch (e) { ctx.fillStyle = '#111'; ctx.fillRect(0, 0, OW, OH); }
    ctx.globalAlpha = opacity;
    ctx.drawImage(canvasRef.current, 0, 0, OW, OH);
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#000a'; ctx.fillRect(0, OH - 44, OW, 44);
    ctx.fillStyle = '#fff'; ctx.font = '600 22px Poppins, Arial';
    ctx.fillText(`Mapa de calor · ${famLabel(fam)} · ${rangeLabel} · Lisual`, 18, OH - 15);
    try {
      out.toBlob((b) => { const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = `mapa-de-calor-${fam}.png`; a.click(); });
    } catch (e) { alert('No se pudo exportar la imagen desde este navegador.'); }
  };

  if (!photos.length) return <div style={{ color: T.muted, padding: 24, textAlign: 'center' }}>No hay fotos en este intervalo.</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <Seg value={fam} onChange={setFam} options={[{ value: 'todo', label: 'Todo' }, ...FAMILIES.map((f) => ({ value: f.key, label: f.label }))]} />
        <Seg value={mode} onChange={setMode} options={[{ value: 'total', label: 'Período completo' }, { value: 'evol', label: 'Evolución' }]} />
        <div style={{ flex: 1 }} />
        {mode === 'total' && <Btn onClick={download} disabled={!url} title="Descargar imagen para compartir">⬇ Descargar</Btn>}
      </div>

      {mode === 'total' ? (
        <>
          <div style={{ position: 'relative', width: '100%', aspectRatio: `${W} / ${H}`, borderRadius: 12, overflow: 'hidden', background: '#000' }}>
            {url && <img src={url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', filter: 'saturate(.7) brightness(.85)' }} />}
            <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity, mixBlendMode: 'normal' }} />
            {empty && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#fff', textShadow: '0 1px 4px #000' }}>Sin detecciones de {famLabel(fam).toLowerCase()} en este período</div>}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginTop: 12, fontSize: 12, color: T.muted }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              poco <span style={{ width: 90, height: 8, borderRadius: 4, background: 'linear-gradient(90deg,#0040ff,#00e5ff,#2BE88B,#ffd23f,#ff2a2a)' }} /> mucho
            </span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Contraste
              <input type="range" min={0.25} max={1.6} step={0.05} value={1.85 - gamma} onChange={(e) => setGamma(1.85 - +e.target.value)} style={{ accentColor: T.blue }} />
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Opacidad {Math.round(opacity * 100)}%
              <input type="range" min={0.1} max={1} step={0.05} value={opacity} onChange={(e) => setOpacity(+e.target.value)} style={{ accentColor: T.blue }} />
            </label>
            <span>Densidad acumulada de {photos.length} fotos · rojo = donde más se concentró, no todo lo que pasó.</span>
          </div>
        </>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
          {slices.map((s) => <Mini key={s.label} slice={s} cfg={cfg} fam={fam} url={url} bg={bg} gamma={gamma} opacity={opacity} />)}
        </div>
      )}
    </div>
  );
}

function Mini({ slice, cfg, fam, url, bg, gamma, opacity }) {
  const ref = useRef(null);
  const W = 200, H = Math.round((W * bg.h) / bg.w);
  useEffect(() => { if (ref.current) paint(ref.current, accumulate(slice.photos, cfg, fam, W, H), W, H, gamma); }, [slice, cfg, fam, W, H, gamma]);
  return (
    <div>
      <div style={{ position: 'relative', width: '100%', aspectRatio: `${W} / ${H}`, borderRadius: 10, overflow: 'hidden', background: '#000' }}>
        {url && <img src={url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', filter: 'saturate(.6) brightness(.8)' }} />}
        <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity }} />
      </div>
      <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>{slice.label} · {slice.photos.length} fotos</div>
    </div>
  );
}

const famLabel = (f) => (f === 'todo' ? 'Todo' : FAMILIES.find((x) => x.key === f)?.label || f);
